const API_BASE = import.meta.env.VITE_API_URL || 'https://jizhu.huanyan.org';
import { getToken } from './storage';

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  // Explicit `new ArrayBuffer` (not rawData.length) so the underlying
  // buffer is typed as `ArrayBuffer` rather than `ArrayBufferLike`.
  // PushManager.subscribe's `applicationServerKey` requires
  // `BufferSource` (a stricter view) under TS 5.7+ lib types.
  const out = new Uint8Array(new ArrayBuffer(rawData.length));
  for (let i = 0; i < rawData.length; ++i) out[i] = rawData.charCodeAt(i);
  return out;
}

function withTimeout<T>(p: Promise<T>, ms: number, msg: string): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(msg)), ms);
    p.then(
      (v) => { clearTimeout(t); resolve(v); },
      (e) => { clearTimeout(t); reject(e); }
    );
  });
}

export async function requestNotificationPermission(): Promise<boolean> {
  if (!('Notification' in window)) {
    throw new Error('此浏览器不支持通知');
  }
  if (Notification.permission === 'granted') {
    await subscribeForPush();
    return true;
  }
  if (Notification.permission === 'denied') {
    throw new Error('通知已被禁用,请在浏览器设置中开启');
  }
  const result = await Notification.requestPermission();
  if (result === 'granted') {
    await subscribeForPush();
    return true;
  }
  return false;
}

async function subscribeForPush(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    throw new Error('此浏览器不支持 Service Worker');
  }
  const reg = await navigator.serviceWorker.ready;
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    const keyRes = await fetch(API_BASE + '/api/push/vapid-public-key');
    if (!keyRes.ok) throw new Error('获取 VAPID 公钥失败: HTTP ' + keyRes.status);
    const { publicKey } = await keyRes.json();
    if (!publicKey) throw new Error('VAPID 公钥为空');
    try {
      sub = await withTimeout(
        reg.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(publicKey),
        }),
        10000,
        '浏览器 10 秒内未完成推送订阅(可能浏览器不支持 Web Push 或 Push Service 不可用)'
      );
    } catch (e: any) {
      throw new Error('浏览器拒绝订阅推送: ' + (e?.message || e));
    }
  }
  const token = getToken();
  if (!token) {
    throw new Error('未登录,请先登录账号');
  }
  const saveRes = await fetch(API_BASE + '/api/push/subscribe', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
    body: JSON.stringify(sub.toJSON()),
  });
  if (!saveRes.ok) {
    const text = await saveRes.text().catch(() => '');
    throw new Error('保存订阅失败: HTTP ' + saveRes.status + (text ? ' ' + text : ''));
  }
}
